import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  AtsJobRequisitionsHiringManagersDocument,
  AtsJobRequisitionsListDocument,
  AtsJobRequisitionsLocationsDocument,
  AtsJobRequisitionsRecruitersDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { requisitionStatusLabel } from 'modules/ats/lib/utils/requisitionStatus'

const requisitionStatusToColor: Record<string, StatusVariant> = {
  pending: 'warning',
  approved: 'positive',
  cancelled: 'neutral',
  rejected: 'critical',
}

export const RequisitionsTable = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()

  const source = useGraphqlDataCollectionSource({
    document: AtsJobRequisitionsListDocument,
    mapData: (data) => data.ats.requisitionsConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.requisitionsConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, filters, search }) => {
      const status = filters?.status?.map(String).filter(Boolean) || undefined
      const hiringManagerIds = filters?.hiringManagerIds?.map(Number).filter(Boolean) || undefined
      const recruiterIds = filters?.recruiterIds?.map(Number).filter(Boolean) || undefined
      const workplaceIds = filters?.workplaceIds?.map(Number).filter(Boolean) || undefined

      return {
        sort: sortings?.[0],
        status,
        hiringManagerIds,
        recruiterIds,
        workplaceIds,
        search,
      }
    },
    search: {
      enabled: true,
    },
    sortings: {
      jobPostingTitle: {
        label: i18n.t('ats.jobs_requisitions.table_headers.jobs'),
        sortBy: 'jobPostingTitle',
      },
      createdAt: {
        label: i18n.t('ats.jobs_requisitions.table_headers.requested_on'),
        sortBy: 'createdAt',
      },
      status: {
        label: i18n.t('ats.jobs_requisitions.table_headers.status'),
        sortBy: 'status',
      },
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    filters: {
      status: {
        label: i18n.t('ats.jobs_requisitions.table_headers.status'),
        type: 'in',
        options: {
          options: [
            {
              label: i18n.t('ats.jobs_requisitions.status.pending'),
              value: 'pending',
            },
            {
              label: i18n.t('ats.jobs_requisitions.status.approved'),
              value: 'approved',
            },
            {
              label: i18n.t('ats.jobs_requisitions.status.cancelled'),
              value: 'cancelled',
            },
            {
              label: i18n.t('ats.jobs_requisitions.status.rejected'),
              value: 'rejected',
            },
          ],
        },
      },
      hiringManagerIds: {
        label: i18n.t('ats.jobs_requisitions.table_headers.hiring_manager'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobRequisitionsHiringManagersDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.hiringManagersConnection.nodes.map((manager) => ({
                label: manager.access?.fullName || '',
                value: Number(manager.access?.id),
              })) || []
            )
          },
        },
      },
      recruiterIds: {
        label: i18n.t('ats.jobs_requisitions.table_headers.recruiter'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobRequisitionsRecruitersDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.employees.employeesConnection.nodes.map((recruiter) => ({
                label: recruiter.access?.fullName || '',
                value: Number(recruiter.access?.id),
              })) || []
            )
          },
        },
      },
      workplaceIds: {
        label: i18n.t('ats.jobs_requisitions.table_headers.location'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobRequisitionsLocationsDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.locations.locationsConnection.nodes.map((location) => ({
                label: location.name || '',
                value: Number(location.id),
              })) || []
            )
          },
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.jobs_requisitions.status.pending'),
        filter: {
          status: ['pending'],
        },
      },
      {
        label: i18n.t('ats.jobs_requisitions.status.approved'),
        filter: {
          status: ['approved'],
        },
      },
      {
        label: i18n.t('ats.jobs_requisitions.status.cancelled'),
        filter: {
          status: ['cancelled'],
        },
      },
      {
        label: i18n.t('ats.jobs_requisitions.status.rejected'),
        filter: {
          status: ['rejected'],
        },
      },
    ],
    itemUrl: (requisition) =>
      navigation.ats.jobs.requisitions.requisitionDetail.fullPath({
        requisition_id: requisition.id,
      }),
    primaryActions: () => ({
      label: i18n.t('ats.jobs.request_new_job'),
      icon: Add,
      onClick: () => navigate(navigation.ats.jobs.requisitions.new.fullPath()),
    }),
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.jobs'),
                render: ({ jobPostingTitle }) => jobPostingTitle,
                sorting: 'jobPostingTitle',
              },
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.location'),
                render: ({ workplace }) => workplace?.name ?? '',
              },
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.hiring_manager'),
                render: ({ hiringManager }) => ({
                  type: 'person',
                  value: {
                    firstName: hiringManager?.fullName ?? '',
                    lastName: '',
                    src: buildUrl(hiringManager?.avatar),
                  },
                }),
              },
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.recruiter'),
                render: ({ recruiter }) => ({
                  type: 'person',
                  value: {
                    firstName: recruiter?.fullName ?? '',
                    lastName: '',
                    src: buildUrl(recruiter?.avatar),
                  },
                }),
              },
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.requested_on'),
                render: ({ createdAt }) => dateBackendDateTimeToRelativeTime(createdAt),
                sorting: 'createdAt',
              },
              {
                label: i18n.t('ats.jobs_requisitions.table_headers.status'),
                render: ({ status }) => ({
                  type: 'status',
                  value: {
                    status: requisitionStatusToColor[status] ?? 'neutral',
                    label:
                      requisitionStatusLabel(status) ??
                      i18n.t('ats.jobs_requisitions.status.undefined'),
                  },
                }),
                sorting: 'status',
              },
            ],
          },
        },
      ]}
    />
  )
}
